import React, { type ComponentProps, ReactNode, useMemo } from 'react';

import { CSSProp } from 'styled-components';

import { useMultiAutocomplete } from '../hooks';
import { GetSelectedItemProps, ItemToString, RenderMenuItem } from '../types';
import type {
  GenericItem,
  UseMultiAutocompleteProps,
  VirtualProps,
  RenderGroupHeaderProps,
} from '../types';
import MultiAutocompleteControl from './Control/MultiAutocompleteControl';
import DropdownMenu from './Menu/DropdownMenu';
import { filterUndefined } from '../helpers/filterUndefined';

type ControlProps<TItem extends GenericItem> = ComponentProps<
  typeof MultiAutocompleteControl<TItem>
>;

type MenuProps<TItem extends GenericItem> = ComponentProps<
  typeof DropdownMenu<TItem>
>;

export type MultiAutocompleteTemplateProps<TItem extends GenericItem> =
  UseMultiAutocompleteProps<TItem> & {
    style?: CSSProp;
    /**
     * Disable the entire dropdown
     */
    disabled?: boolean;
    /**
     * Enable error state
     */
    error?: boolean;
    /**
     * Enable loading state
     */
    isLoading?: boolean;
    /**
     * Placeholder text to display
     */
    placeholder?: string;
    /**
     * Function to render a custom dropdown control
     * @param args Props to be provided to the control
     * @returns A dropdown control element
     */
    renderControl?: (args: ControlProps<TItem>) => React.ReactNode;
    /**
     * Function to render a custom dropdown menu
     * @param args Props to be provided to the menu
     * @param virtualProps Props used to virtualize the menu - see stories for examples
     * @returns A dropdown menu element
     */
    renderMenu?: (
      args: MenuProps<TItem>,
      virtualProps: VirtualProps,
    ) => React.ReactNode;
    /**
     * Function to render custom selected items
     * @param isOpen Whether the dropdown is open
     * @param selectedItems The selected items
     * @param getSelectedItemProps Function to get props for a selected item
     * @param removeSelectedItem Function to remove a selected item
     * @param itemToString Function to convert an item to a string
     * @param disabled Whether the dropdown is disabled
     */
    renderItems?: ({
      isOpen,
      selectedItems,
      getSelectedItemProps,
      removeSelectedItem,
      itemToString,
      disabled,
    }: {
      getSelectedItemProps: GetSelectedItemProps<TItem>;
      isOpen: boolean;
      selectedItems: TItem[];
      removeSelectedItem: (item: TItem) => void;
      itemToString: ItemToString<TItem>;
      disabled?: boolean;
    }) => ReactNode;
    /**
     * Function to render a custom menu item
     */
    renderMenuItem?: RenderMenuItem<TItem>;
    /**
     * Function to render a custom group header menu item
     */
    renderGroupHeader?: (props: RenderGroupHeaderProps) => ReactNode;
    /**
     * Function to render a component that will appear when there are no menu items
     */
    renderNoAutocompleteResults?: () => ReactNode;
    /**
     * Enable highlighting of search text in menu items
     */
    enableSearchHighlight?: boolean;
  };

const defaultRenderControl = <TItem extends GenericItem>(
  args: ControlProps<TItem>,
) => <MultiAutocompleteControl {...args} />;

/**
 * A multi select, searchable dropdown component.
 *
 * This component covers the majority of use cases, but advanced use cases may require using the `useMultiAutocomplete` hook directly.
 */
const Autocomplete = <TItem extends GenericItem>({
  style,
  disabled,
  error,
  isLoading,
  placeholder,
  renderControl = defaultRenderControl,
  renderMenu = DropdownMenu.render,
  renderItems,
  renderMenuItem,
  renderGroupHeader,
  renderNoAutocompleteResults,
  enableSearchHighlight,
  ...args
}: MultiAutocompleteTemplateProps<TItem>) => {
  const { controlProps, menuProps, virtualProps } = useMultiAutocomplete(args);

  // Filter out undefined props to ensure they are not passed to the control
  // as explicitly undefined and overwrite other props
  const filteredControlProps = useMemo(
    () =>
      filterUndefined({
        disabled,
        error,
        isLoading,
        placeholder,
        renderItems,
      }),
    [disabled, error, isLoading, placeholder, renderItems],
  );
  const filteredMenuProps = useMemo(
    () =>
      filterUndefined({
        renderMenuItem,
        renderGroupHeader,
        renderNoAutocompleteResults,
        enableSearchHighlight,
      }),
    [
      renderMenuItem,
      renderGroupHeader,
      renderNoAutocompleteResults,
      enableSearchHighlight,
    ],
  );

  return (
    <div css={[{ width: 300 }, style]}>
      {renderControl({
        ...filteredControlProps,
        ...controlProps,
      })}
      {renderMenu({ ...menuProps, ...filteredMenuProps }, virtualProps)}
    </div>
  );
};

export default Autocomplete;
